import { FormSdpModel, InitSDPForm } from '@services/model/form/form.SDP.model'
import { Fragment, useContext, useEffect, useState, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Typography } from '@mui/material'
import FormField from '@components/form/field'
import FormContainer from '@components/form/container'
import { FormPartComponents } from '@models/common'
import { useTranslation } from 'react-i18next'
import useQuery from '@hooks/useQuery'
import { QueryStepParser } from '@utils/queryStepParser'
import FormController from '@components/form/controller'
import FormSelect from '@components/form/select'
import uuid from 'react-uuid'
import { FormContext } from '@providers/formStateProvider'
import { FormSDModel, SDGeneralOptions } from '@services/model/form/form.SD.model'
import { NavigateTo } from '@utils/navigate'
import FormMediumMap from '@components/form/mediumMap'
import FormImageUpload from '@components/form/imageUpload'
import useFormSwitcher from '@hooks/useFormSwitcher'
import FormSDService from '@services/formService/form.SD.service'
import { ImageModel } from '@services/model/image.model'
import { FormStatusEnum } from '@services/model/form/form.model'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import ValidationToast from '@components/form/ValidationToast'
import { canvasExport } from '@utils/canvasExport'

const UUID = uuid()
const SDPForm = () => {
  const { state, dispatch } = useContext(FormContext)

  // extracting id from params
  const { id } = useParams<string>()

  // extracting index from query
  const query = useQuery()
  const num = QueryStepParser(query.get('num'))

  const { t } = useTranslation()
  const navigate = useNavigate()
  const [parentForm, setParentForm] = useState<FormSDModel | undefined>()
  const [sdGeneralOptions, setSdGeneralOptions] = useState<SDGeneralOptions>()
  const mapRef = useRef<HTMLUListElement>(null)
  const saveTargetMap = async () => {
    let targetBase64 = await canvasExport(mapRef)
    setValue('locationMapBase64', targetBase64)
  }

  const editable =
    (parentForm?.baseForm.formStatus || FormStatusEnum.FORM_SD_DRAFT) ===
      FormStatusEnum.FORM_SD_DRAFT ||
    (parentForm?.baseForm.formStatus || FormStatusEnum.FORM_SD_REJECTED) ===
      FormStatusEnum.FORM_SD_REJECTED

  const validationSchema = yup
    .object()
    .shape({
      photoCategoryId: yup
        .number()
        .transform((value) =>
          isNaN(value) || value === null || value === undefined ? undefined : value,
        )
        .required(t('Category is required')),
      location: yup.object().required(t('Location is required')),
    })
    .required()

  const { control, setValue, getValues, trigger, reset } = useForm<FormSdpModel>({
    defaultValues: { ...InitSDPForm },
    resolver: yupResolver(validationSchema),
    mode: 'all',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
  })

  const reloadOthers = async () => {
    const options = await FormSDService.GetGeneralOptions()
    setSdGeneralOptions(options)
  }

  useEffect(() => {
    if (state.form && state.formType === 'SD') {
      if (num !== undefined) {
        // old form
        const parentSDForm = state.form as FormSDModel
        if (parentSDForm.sdPhotos && parentSDForm.sdPhotos[num]) {
          const thisForm = parentSDForm.sdPhotos[num]

          reset(thisForm)
        }
      } else {
        // new form
        reset({ ...InitSDPForm })
      }
      reloadOthers()
      // set parent form into this state
      setParentForm(state.form)
    } else {
      // redirect to SD form
      NavigateTo(navigate, '/site-diary/:id', { id }, { step: 4 })
    }

    // pop off form stack
    return () => dispatch({ type: 'pop', targetFormType: 'SD' })
  }, [id, num, dispatch])
  //#region form parts components

  //#region part a
  const PartA = (): FormPartComponents => {
    return {
      partTitle: t('General Information'),
      component: (
        <FormContainer fkey={`${UUID}-sdp-a`}>
          <FormField fieldName="Site Diary No.">
            <Typography>{getValues('sdNo')}</Typography>
          </FormField>

          <FormField fieldName="Site Diary Photo No.">
            <Typography>{getValues('sdPhotoNo')}</Typography>
          </FormField>

          <FormField fieldName="Category">
            <FormController
              controllerProps={{
                name: 'photoCategoryId',
                control: control,
              }}>
              <FormSelect
                fullWidth
                options={
                  sdGeneralOptions?.photoCategoryList?.map((x) => {
                    return {
                      key: x.id,
                      value: x.name,
                    }
                  }) || []
                }
                uneditable={!editable}
              />
            </FormController>
          </FormField>

          <FormField fieldName="Image">
            <FormController
              controllerProps={{
                name: 'images',
                control: control,
              }}>
              <FormImageUpload
                additionalChangeAction={async (value: ImageModel[]) => {
                  if (value.length > 0 && value[value.length-1].location) {
                    setValue('location', value[value.length-1].location)
                    setValue('locationMapBase64', await canvasExport(mapRef))
                  }
                }}
                uneditable={!editable}
                multiple
              />
            </FormController>
          </FormField>

          <FormField fieldName="Location">
            <FormController
              controllerProps={{
                name: 'location',
                control: control,
              }}>
              <FormMediumMap 
                uneditable={!editable} 
                additionalChangeAction={async () => {
                  setValue('locationMapBase64', await canvasExport(mapRef))
                }}
                ref={mapRef}
              />
            </FormController>
          </FormField>
        </FormContainer>
      ),
      onNext: () => saveTargetMap()
    }
  }
  //#endregion

  //#endregion

  const handleOnSubmit = async (
    event: any,
    isReject: boolean,
    notifyList?: string[],
    signatureBase64?: string,
  ) => {
    if (!(await trigger())) return

    if (parentForm) {
      let _parentForm: FormSDModel = { ...parentForm }
      if (num !== undefined) {
        // old form
        _parentForm = {
          ...parentForm,
          sdPhotos: parentForm.sdPhotos?.map((photo: FormSdpModel, index: number) =>
            index === num ? getValues() : photo,
          ),
        }
      } else {
        // new form
        _parentForm = {
          ...parentForm,
          sdPhotos: [...(parentForm.sdPhotos ?? []), getValues()],
        }
      }
      // insert this form to parent from
      dispatch({ type: 'push', form: _parentForm, formType: 'SD' })
    } else {
      alert('something went wrong')
      dispatch({ type: 'clear' })
    }

    NavigateTo(navigate, '/site-diary/:id', { id }, { step: 4 })
  }

  const [FormSwitcher] = useFormSwitcher({
    title: t('SITE DIARY PHOTO'),
    components: [PartA()],
    formOnSubmit: handleOnSubmit,
    disableComment: true,
    navigateBack: () => {
      if (parentForm) {
        let _parentForm: FormSDModel = { ...parentForm }
        dispatch({ type: 'push', form: _parentForm, formType: 'SD' })
        NavigateTo(navigate, '/site-diary/:formId', { id }, { step: 4 })
      } else {
        dispatch({ type: 'clear' })
        NavigateTo(navigate, '/site-diary/all-record')
      }
    },
    ...(parentForm && {
      onSubFormComplete: async () => {
        if (!(await trigger())) return
        await saveTargetMap()
        if (parentForm) {
          let _parentForm: FormSDModel = { ...parentForm }
          if (num !== undefined) {
            // old form
            _parentForm = {
              ...parentForm,
              sdPhotos: parentForm.sdPhotos?.map((photo: FormSdpModel, index: number) =>
                index === num ? getValues() : photo,
              ),
            }
          } else {
            // new form
            _parentForm = {
              ...parentForm,
              sdPhotos: [...(parentForm.sdPhotos ?? []), getValues()],
            }
          }
          // insert this form to parent from
          dispatch({ type: 'push', form: _parentForm, formType: 'SD' })
        } else {
          alert('something went wrong')
          dispatch({ type: 'clear' })
        }

        NavigateTo(navigate, '/site-diary/:formId', { id }, { step: 4 })
      },
    }),
  })

  return (
    <Fragment>
      <FormSwitcher />
      <ValidationToast control={control} />
    </Fragment>
  )
}

export default SDPForm
